import React, { ReactElement, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import {
  useForm,
} from 'react-hook-form';
import {
  Button,
  Card,
  Form,
  Spinner,
} from 'react-bootstrap';
import { useSWRConfig } from 'swr';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useHasRoles } from 'hooks/useHasRoles';
import { useWeeklyPullSchedule } from 'hooks/V1SyncHooks';
import {
  putWeeklyPullSchedule,
  deleteWeeklyPullSchedule,
} from 'api/V1SyncAPI';
import { WeeklyPullScheduleRequest } from 'types/V1SyncTypes';
import { Input, Checkbox } from 'components/FormControls';
import { DeleteActionModal } from 'components/DeleteActionModal';

interface FormValues {
  executionTimeUtc: string
  startDateUtc: string
  repeatEveryDays: number
  isActive: boolean
}

const defaultFormValues: FormValues = {
  executionTimeUtc: '02:00',
  startDateUtc: moment().utc().format('YYYY-MM-DD'),
  repeatEveryDays: 7,
  isActive: true,
};

function formatTimeForInput(executionTimeUtc: string): string {
  if (!executionTimeUtc) return '02:00';
  const parts = executionTimeUtc.split(':');
  return parts.length >= 2 ? `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}` : '02:00';
}

function formatTimeForApi(hhmm: string): string {
  if (!hhmm) return '02:00:00';
  return hhmm.length === 5 ? `${hhmm}:00` : hhmm;
}

function formatDateForInput(startDateUtc: string): string {
  if (!startDateUtc) return moment().utc().format('YYYY-MM-DD');
  const datePart = startDateUtc.split('T')[0];
  return datePart || moment().utc().format('YYYY-MM-DD');
}

const V1SyncAutomationPage = (): ReactElement => {
  const { tenantId } = useParams<{
    tenantId?: string
  }>();
  const history = useHistory();
  const { mutate: globalMutate } = useSWRConfig();
  const [isSuper] = useHasRoles(['Super']);

  const tenantIdNumber = tenantId ? Number(tenantId) : 0;
  const {
    data: schedule,
    error,
    isLoading,
    mutate,
  } = useWeeklyPullSchedule(tenantIdNumber);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (schedule) {
      reset({
        executionTimeUtc: formatTimeForInput(schedule.ExecutionTimeUtc),
        startDateUtc: formatDateForInput(schedule.StartDateUtc),
        repeatEveryDays: schedule.RepeatEveryDays,
        isActive: schedule.IsActive,
      });
    } else if (!isLoading && !error) {
      reset(defaultFormValues);
    }
  }, [schedule, isLoading, error, reset]);

  if (!isSuper) {
    return <Redirect to="/" />;
  }

  if (!tenantId || Number.isNaN(tenantIdNumber)) {
    return <Redirect to="/v1sync" />;
  }

  const onSave = async (data: FormValues) => {
    setApiError(null);
    setSaving(true);
    try {
      const body: WeeklyPullScheduleRequest = {
        ExecutionTimeUtc: formatTimeForApi(data.executionTimeUtc),
        StartDateUtc: data.startDateUtc,
        RepeatEveryDays: Number(data.repeatEveryDays),
        IsActive: data.isActive,
      };
      await putWeeklyPullSchedule(tenantIdNumber, body);
      toast.success('Schedule saved.');
      mutate();
      globalMutate(['tenantIdsWithConfigs']);
    } catch (err: any) {
      const message = err?.response?.data?.Message || err?.message || 'Failed to save schedule.';
      setApiError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await deleteWeeklyPullSchedule(tenantIdNumber);
      toast.success('Schedule removed. No further automated runs will occur.');
      setShowDeleteConfirm(false);
      mutate(null, false);
      globalMutate(['tenantIdsWithConfigs']);
    } catch (err: any) {
      const message = err?.response?.data?.Message || err?.message || 'Failed to delete schedule.';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const title = `V1 Sync – Automation – Tenant ${tenantId}`;

  if (error && error?.response?.status !== 404) {
    return (
      <div className="broadcast-forms Templates">
        <Helmet><title>{title}</title></Helmet>
        <p className="text-danger">{error?.response?.data?.Message || 'An error occurred loading the schedule.'}</p>
        <Button variant="link" onClick={() => history.push('/v1sync')}>Back to V1 Sync configs</Button>
      </div>
    );
  }

  return (
    <div className="broadcast-forms Templates">
      <Helmet><title>{title}</title></Helmet>

      <div className="d-flex align-items-center mb-3">
        <Button
          variant="link"
          className="p-0 mr-3"
          onClick={() => history.push('/v1sync')}
        >
          ← Back to V1 Sync configs
        </Button>
      </div>

      <h3>{title}</h3>

      <p className="text-muted mb-4">
        Scheduled runs are checked hourly; your job will run at the next check after its scheduled time.
      </p>

      {isLoading ? (
        <Spinner animation="border" />
      ) : (
        <>
          {schedule && (
            <Card className="mb-4">
              <Card.Header as="h5">Current schedule</Card.Header>
              <Card.Body>
                <p className="mb-1">
                  <strong>Active:</strong>
                  {' '}
                  {schedule.IsActive ? 'Yes' : 'No'}
                </p>
                <p className="mb-1">
                  <strong>Execution time (UTC):</strong>
                  {' '}
                  {schedule.ExecutionTimeUtc}
                </p>
                <p className="mb-1">
                  <strong>Start date:</strong>
                  {' '}
                  {formatDateForInput(schedule.StartDateUtc)}
                </p>
                <p className="mb-1">
                  <strong>Repeat every:</strong>
                  {' '}
                  {schedule.RepeatEveryDays}
                  {' '}
                  days
                </p>
                {schedule.LastScheduledRunUtc && (
                  <p className="mb-1">
                    <strong>Last run (UTC):</strong>
                    {' '}
                    {moment.utc(schedule.LastScheduledRunUtc).format('YYYY-MM-DD, h:mm a')}
                  </p>
                )}
                {schedule.NextRunUtc && schedule.IsActive && (
                  <p className="mb-1">
                    <strong>Next run (UTC):</strong>
                    {' '}
                    {moment.utc(schedule.NextRunUtc).format('YYYY-MM-DD, h:mm a')}
                  </p>
                )}
              </Card.Body>
            </Card>
          )}

          {!schedule && (
            <Card className="mb-4 border-warning">
              <Card.Body>No automation configured. Use the form below to create a schedule.</Card.Body>
            </Card>
          )}

          <Card>
            <Card.Header as="h5">{schedule ? 'Edit schedule' : 'Create schedule'}</Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit(onSave)}>
                <Form.Group>
                  <Input
                    id="executionTimeUtc"
                    name="executionTimeUtc"
                    label="Execution time (UTC)"
                    type="time"
                    control={control}
                    rules={{ required: 'Required' }}
                    errors={errors.executionTimeUtc?.message}
                  />
                </Form.Group>
                <Form.Group>
                  <Input
                    id="startDateUtc"
                    name="startDateUtc"
                    label="Start date"
                    type="date"
                    control={control}
                    rules={{ required: 'Required' }}
                    errors={errors.startDateUtc?.message}
                  />
                </Form.Group>
                <Form.Group>
                  <Input
                    id="repeatEveryDays"
                    name="repeatEveryDays"
                    label="Repeat every (days)"
                    type="number"
                    min={1}
                    control={control}
                    rules={{
                      required: 'Required',
                      min: { value: 1, message: 'Must be at least 1' },
                    }}
                    errors={errors.repeatEveryDays?.message}
                  />
                </Form.Group>
                <Form.Group>
                  <Checkbox
                    id="isActive"
                    name="isActive"
                    label="Active"
                    control={control}
                  />
                </Form.Group>
                {apiError && <p className="text-danger">{apiError}</p>}
                <div className="d-flex align-items-center gap-3">
                  <Button type="submit" variant="primary" disabled={saving}>
                    {saving && <Spinner as="span" animation="border" size="sm" className="mr-2" />}
                    Save
                  </Button>
                  {schedule && (
                    <Button
                      type="button"
                      variant="outline-danger"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete schedule
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </>
      )}

      <DeleteActionModal
        isOpen={showDeleteConfirm}
        title="Remove automation schedule?"
        buttonName="Delete schedule"
        isDeleting={deleting}
        onCancel={() => setShowDeleteConfirm(false)}
        onSuccess={onDelete}
      >
        <p>This will stop all future automated runs for this tenant. You can create a new schedule at any time.</p>
      </DeleteActionModal>
    </div>
  );
};

export default V1SyncAutomationPage;
